import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import type { Maybe } from '@/lib/types';
import { theme } from '@/theme';
import { scrollbar } from '@/theme/scrollbar';

import { getImageAndBackgroundStyles } from '../commonStyles.styles';

export const Header = styled(Box)<{
  $backgroundColorOpacity: Maybe<string>;
  backgroundColor: Maybe<string>;
  backgroundImage: Maybe<string>;
}>`
  padding: ${({ theme }) => theme.spacing(1, 0)};

  height: 375px;
  isolation: isolate;

  ${({ backgroundImage }) =>
    backgroundImage &&
    css`
      background-repeat: no-repeat;
      background-size: cover;
      background-position: bottom 0 center;
    `}

  ${({ $backgroundColorOpacity, backgroundColor, backgroundImage }) =>
    getImageAndBackgroundStyles(
      $backgroundColorOpacity,
      backgroundColor,
      backgroundImage,
    )} 

  @media (min-width: 769px) and (max-width: 991px) {
    height: 315px;
  }

  ${theme.media.md.up} {
    height: 304px;
  }
`;

export const Heading = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  min-height: 170px;
  margin-top: -25px;
  margin-bottom: 15px;

  ${({ theme }) => css`
    ${theme.media.md.up} {
      min-height: 125px;
      margin-bottom: -${theme.spacing(2)};
    }
  `}

  @media (max-width: 768px) {
    margin-top: -30px;
    margin-bottom: 20px;
  }
`;

export const ImageContainer = styled.div`
  position: relative;
  margin-top: -45px;
  width: 90px;
  min-width: 90px;
  text-align: center;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: 1225px) {
    width: 100px;
    min-width: 100px;
  }

  @media (max-width: 768px) {
    width: auto;
    min-width: auto;
  }
`;

export const ScrollableRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  justify-content: space-between;
  column-gap: 15px;

  ${({ theme }) => css`
    padding-bottom: ${theme.spacing(1)};
  `}

  ${({ theme }) => css`
    ${theme.media.md.up} {
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }
  `}

  @media (min-width: 769px) and (max-width: 991px) {
    ${scrollbar}
    scrollbar-width: thin;
    margin-top: -60px;

    &::-webkit-scrollbar {
      height: 5px;
    }
  }

  @media (max-width: 768px) {
    ${scrollbar}
    scrollbar-width: thin;
    margin-top: -60px;
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
    column-gap: 5px;

    &::-webkit-scrollbar {
      height: 5px;
    }
  }
`;

export const CollectionName = styled.span`
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 100%;
  min-height: 45px;
  padding: ${({ theme }) => theme.spacing(0.5, 0.5, 1)};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    line-height: 1.2;
    color: ${theme.colors.navyBlue};
  `}

  & > span {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;
    hyphens: auto;
  }
`;
